import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useDebounce } from '@app/react-common/hooks/useDebounce';
import Button from '@app/react-common/components/button/Button';
import { Checkbox } from '@app/react-common/components/checkbox/Checkbox';
import CountriesAndRegionsDropdown from '@app/react-common/components/countries-and-regions-dropdown/CountriesAndRegionsDropdown';
import Divider from '@app/react-common/components/divider/Divider';
import Input from '@app/react-common/components/input/Input';
import RadioButtonGroup, { RadioButtonOption } from '@app/react-common/components/radio-button-group/RadioButtonGroup';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';
import { SelectedMethodInfo, DeliveryAreaModeGroup, SUPPORTED_DELIVERY_AREA_METHODS } from '../BulkEditModal';
import { extendedCountryToStates, convertLegacyStatesToControl } from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { isInternationalMethod } from '../utils/bulkEditUtils';
import { SHIP_TO_ACTIONS } from '../utils/consts';

export type ShipToAction = 'add' | 'remove';

interface ShipToEditViewProps {
  onBack: () => void;
  onClose: () => void;
  onPreviewChanges: (data: ShipToFormData) => void;
  selectedMethods: SelectedMethodInfo[];
  shopCountryCode?: string;
  deliveryAreaMode?: DeliveryAreaModeGroup | null;
}

export interface ShipToFormData {
  action: ShipToAction;
  shopCountryCode?: string;
  statesControl: Record<string, string[]>;
  cities: string[];
  zones?: string[];
  zipCodes?: string[];
  excludedStates?: string[];
}

const getMethodCities = (method: SelectedMethodInfo): string[] => {
  if (isInternationalMethod(method)) return [];
  return method.polygonInputValues?.[0]?.listedCities || [];
};

const getExistingCitiesUnion = (methods: SelectedMethodInfo[]): string[] => {
  const citiesSet = new Set<string>();
  methods.forEach((method) => {
    const cities = getMethodCities(method);
    cities.forEach((city) => citiesSet.add(city));
  });
  return Array.from(citiesSet).sort();
};

const getMethodStatesControl = (method: SelectedMethodInfo, shopCountryCode: string): Record<string, string[]> => {
  if (isInternationalMethod(method)) return {};

  const polygon = method.polygonInputValues?.[0];

  if (polygon?.listedStatesControl && Object.keys(polygon.listedStatesControl).length > 0) {
    return polygon.listedStatesControl;
  }

  if (polygon?.listedStates && polygon.listedStates.length > 0) {
    return convertLegacyStatesToControl(polygon.listedStates, shopCountryCode);
  }

  return {};
};

interface StateItem {
  country: string;
  state: string;
  label: string;
}

const getExistingStatesUnion = (methods: SelectedMethodInfo[], shopCountryCode: string): StateItem[] => {
  const statesMap = new Map<string, StateItem>();
  methods.forEach((method) => {
    const statesControl = getMethodStatesControl(method, shopCountryCode);
    Object.entries(statesControl).forEach(([country, states]) => {
      const countryStates = extendedCountryToStates[country] || [];
      states.forEach((stateCode) => {
        const key = `${country}:${stateCode}`;
        if (!statesMap.has(key)) {
          const stateInfo = countryStates.find((s) => s.value === stateCode);
          statesMap.set(key, {
            country,
            state: stateCode,
            label: stateInfo?.label || stateCode,
          });
        }
      });
    });
  });
  return Array.from(statesMap.values()).sort((a, b) => a.label.localeCompare(b.label));
};

const getMethodZones = (method: SelectedMethodInfo): Array<{ label: string; value: boolean }> => {
  if (isInternationalMethod(method)) return [];
  return method.polygonInputValues?.[0]?.zonesList || [];
};

const getZonesEnabledInAll = (methods: SelectedMethodInfo[]): string[] => {
  if (methods.length === 0) return [];

  const firstMethodZones = getMethodZones(methods[0]);
  const enabledInFirst = firstMethodZones.filter((z) => z.value).map((z) => z.label);

  if (methods.length === 1) return enabledInFirst;

  return enabledInFirst.filter((zone) => {
    return methods.slice(1).every((method) => {
      const methodZones = getMethodZones(method);
      const zoneEntry = methodZones.find((z) => z.label === zone);
      return zoneEntry?.value === true;
    });
  });
};

const getZonesEnabledInAny = (methods: SelectedMethodInfo[]): string[] => {
  const zonesSet = new Set<string>();
  methods.forEach((method) => {
    const zones = getMethodZones(method);
    zones.filter((z) => z.value).forEach((zone) => zonesSet.add(zone.label));
  });
  return Array.from(zonesSet).sort();
};

const getAllZoneLabels = (methods: SelectedMethodInfo[]): string[] => {
  const zonesSet = new Set<string>();
  methods.forEach((method) => {
    const zones = getMethodZones(method);
    zones.forEach((zone) => zonesSet.add(zone.label));
  });
  return Array.from(zonesSet).sort();
};

const getMethodZipCodes = (method: SelectedMethodInfo): string => {
  if (isInternationalMethod(method)) return '';
  return method.polygonInputValues?.[0]?.zipCodes || '';
};

const parseZipCodesString = (zipCodesStr: string): string[] => {
  if (!zipCodesStr.trim()) return [];
  return zipCodesStr
    .split(',')
    .map((code) => code.trim())
    .filter((code) => code.length > 0);
};

const getExistingZipCodesUnion = (methods: SelectedMethodInfo[]): string[] => {
  const zipCodesSet = new Set<string>();
  methods.forEach((method) => {
    const zipCodesStr = getMethodZipCodes(method);
    const zipCodes = parseZipCodesString(zipCodesStr);
    zipCodes.forEach((code) => zipCodesSet.add(code));
  });
  return Array.from(zipCodesSet).sort();
};

const getZipCodesOperator = (methods: SelectedMethodInfo[]): 'includes' | 'excludes' | null => {
  if (methods.length === 0) return null;
  const operator = methods[0].polygonInputValues?.[0]?.zipCodesOperator;
  return operator || null;
};

const getExcludedStatesUnion = (methods: SelectedMethodInfo[]): string[] => {
  const excludedStatesSet = new Set<string>();
  methods.forEach((method) => {
    const excludedStates = method.excludedStates || method.polygonInputValues?.[0]?.excludedStates || [];
    excludedStates.forEach((state) => excludedStatesSet.add(state));
  });
  return Array.from(excludedStatesSet).sort();
};

const getActionOptions = (deliveryAreaMode: DeliveryAreaModeGroup | null | undefined): RadioButtonOption[] => {
  const optionsMap: Record<
    DeliveryAreaModeGroup,
    { addLabel: string; addDesc: string; removeLabel: string; removeDesc: string }
  > = {
    states: {
      addLabel: 'Add To Ship To',
      addDesc: 'Add states to delivery area',
      removeLabel: 'Remove From Ship To',
      removeDesc: 'Remove states from delivery area',
    },
    cities: {
      addLabel: 'Add To Ship To',
      addDesc: 'Add cities to delivery area',
      removeLabel: 'Remove From Ship To',
      removeDesc: 'Remove cities from delivery area',
    },
    zones: {
      addLabel: 'Enable Zones',
      addDesc: 'Enable zones for delivery',
      removeLabel: 'Disable Zones',
      removeDesc: 'Disable zones for delivery',
    },
    zip_codes: {
      addLabel: 'Add To Ship To',
      addDesc: 'Add zip codes to delivery area',
      removeLabel: 'Remove From Ship To',
      removeDesc: 'Remove zip codes from delivery area',
    },
    all_areas: {
      addLabel: 'Exclude States',
      addDesc: 'Add states to the exclusion list (delivery will be blocked to these states)',
      removeLabel: 'Include States',
      removeDesc: 'Remove states from the exclusion list (delivery will be allowed again)',
    },
    custom: {
      addLabel: 'Add To Ship To',
      addDesc: 'Add to delivery area',
      removeLabel: 'Remove From Ship To',
      removeDesc: 'Remove from delivery area',
    },
  };

  const options = deliveryAreaMode
    ? optionsMap[deliveryAreaMode]
    : {
        addLabel: 'Add To Ship To',
        addDesc: 'Add to delivery area',
        removeLabel: 'Remove From Ship To',
        removeDesc: 'Remove from delivery area',
      };

  return [
    { label: options.addLabel, value: SHIP_TO_ACTIONS.ADD, description: options.addDesc },
    { label: options.removeLabel, value: SHIP_TO_ACTIONS.REMOVE, description: options.removeDesc },
  ];
};

const getInfoMessage = (deliveryAreaMode: DeliveryAreaModeGroup | null | undefined, action: ShipToAction): string => {
  if (deliveryAreaMode === 'all_areas') {
    return action === SHIP_TO_ACTIONS.ADD
      ? 'Selected states will be added to the exclusion list'
      : 'Selected states will be removed from the exclusion list';
  }
  if (deliveryAreaMode === 'zones') {
    return action === SHIP_TO_ACTIONS.ADD
      ? 'Selected zones will be enabled for delivery'
      : 'Selected zones will be disabled for delivery';
  }
  return action === SHIP_TO_ACTIONS.ADD
    ? 'Values will be added to existing Ship To settings. Duplicates will be ignored'
    : 'Values will be removed from existing Ship To settings if they exist';
};

export const ShipToEditView = ({
  onBack,
  onClose,
  onPreviewChanges,
  selectedMethods,
  shopCountryCode = '',
  deliveryAreaMode = null,
}: ShipToEditViewProps) => {
  const [action, setAction] = useState<ShipToAction>(SHIP_TO_ACTIONS.ADD);
  const [statesControl, setStatesControl] = useState<Record<string, string[]>>({});
  const [selectedStatesForRemoval, setSelectedStatesForRemoval] = useState<StateItem[]>([]);
  const [citiesInput, setCitiesInput] = useState('');
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedZones, setSelectedZones] = useState<string[]>([]);
  const [zipCodesInput, setZipCodesInput] = useState('');
  const [selectedZipCodes, setSelectedZipCodes] = useState<string[]>([]);
  const [selectedExcludedStatesForRemoval, setSelectedExcludedStatesForRemoval] = useState<string[]>([]);

  const [selectedExcludedStates, setSelectedExcludedStates] = useState<string[]>([]);
  const [stateSearchInput, setStateSearchInput] = useState('');
  const [stateSuggestions, setStateSuggestions] = useState<Array<{ description: string; placeId: string }>>([]);
  const debouncedStateSearch = useDebounce(stateSearchInput, 300);

  const autocompleteServiceRef = useRef<any>(null);
  const placesServiceRef = useRef<any>(null);

  useEffect(() => {
    const google = (window as any).google;
    if (google?.maps?.places) {
      autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
      const div = document.createElement('div');
      placesServiceRef.current = new google.maps.places.PlacesService(div);
    }
  }, []);

  useEffect(() => {
    if (!autocompleteServiceRef.current || debouncedStateSearch.length < 2) {
      setStateSuggestions([]);
      return;
    }
    autocompleteServiceRef.current.getPlacePredictions(
      {
        input: debouncedStateSearch,
        types: ['(regions)'],
        componentRestrictions: shopCountryCode ? { country: shopCountryCode } : undefined,
      },
      (predictions: any[], status: string) => {
        if (status === 'OK' && predictions) {
          setStateSuggestions(predictions.map((p) => ({ description: p.description, placeId: p.place_id })));
        } else {
          setStateSuggestions([]);
        }
      },
    );
  }, [debouncedStateSearch, shopCountryCode]);

  const handleSelectStateSuggestion = useCallback(
    (placeId: string) => {
      if (!placesServiceRef.current) return;
      placesServiceRef.current.getDetails({ placeId, fields: ['address_components'] }, (place: any, status: string) => {
        if (status === 'OK' && place?.address_components) {
          const stateComponent = place.address_components.find((c: any) =>
            c.types.includes('administrative_area_level_1'),
          );
          const stateName = stateComponent?.long_name;
          if (stateName && !selectedExcludedStates.includes(stateName)) {
            setSelectedExcludedStates((prev) => [...prev, stateName]);
          }
        }
        setStateSearchInput('');
        setStateSuggestions([]);
      });
    },
    [selectedExcludedStates],
  );

  const handleRemoveSelectedState = useCallback((state: string) => {
    setSelectedExcludedStates((prev) => prev.filter((s) => s !== state));
  }, []);

  const isInternational = useMemo(() => {
    return selectedMethods.length > 0 && isInternationalMethod(selectedMethods[0]);
  }, [selectedMethods]);

  const hasUnsupportedMethods = useMemo(() => {
    return selectedMethods.some(
      (method) =>
        method.deliveryAreaSelectMethod &&
        !SUPPORTED_DELIVERY_AREA_METHODS.includes(
          method.deliveryAreaSelectMethod as (typeof SUPPORTED_DELIVERY_AREA_METHODS)[number],
        ),
    );
  }, [selectedMethods]);

  const citiesInAnyMethod = useMemo(() => {
    return getExistingCitiesUnion(selectedMethods);
  }, [selectedMethods]);

  const statesInAnyMethod = useMemo(() => {
    return getExistingStatesUnion(selectedMethods, shopCountryCode);
  }, [selectedMethods, shopCountryCode]);

  const zonesEnabledInAll = useMemo(() => {
    return getZonesEnabledInAll(selectedMethods);
  }, [selectedMethods]);

  const zonesEnabledInAny = useMemo(() => {
    return getZonesEnabledInAny(selectedMethods);
  }, [selectedMethods]);

  const allZoneLabels = useMemo(() => {
    return getAllZoneLabels(selectedMethods);
  }, [selectedMethods]);

  const zonesForAdd = useMemo(() => {
    return allZoneLabels.filter((zone) => !zonesEnabledInAll.includes(zone));
  }, [allZoneLabels, zonesEnabledInAll]);

  const zipCodesInAnyMethod = useMemo(() => {
    return getExistingZipCodesUnion(selectedMethods);
  }, [selectedMethods]);

  const zipCodesOperator = useMemo(() => {
    return getZipCodesOperator(selectedMethods);
  }, [selectedMethods]);

  const excludedStatesInAnyMethod = useMemo(() => {
    return getExcludedStatesUnion(selectedMethods);
  }, [selectedMethods]);

  const parseCommaSeparated = (input: string): string[] => {
    if (!input.trim()) return [];
    return input
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  };

  const shouldShowStates = !deliveryAreaMode || deliveryAreaMode === 'states';
  const shouldShowCities = !deliveryAreaMode || deliveryAreaMode === 'cities';
  const shouldShowZones = deliveryAreaMode === 'zones';
  const shouldShowZipCodes = deliveryAreaMode === 'zip_codes';
  const shouldShowExcludedStates = deliveryAreaMode === 'all_areas';

  const hasChanges = useMemo(() => {
    const hasStates =
      action === SHIP_TO_ACTIONS.ADD
        ? Object.keys(statesControl).length > 0 && Object.values(statesControl).some((states) => states.length > 0)
        : selectedStatesForRemoval.length > 0;
    const hasCities =
      action === SHIP_TO_ACTIONS.ADD ? parseCommaSeparated(citiesInput).length > 0 : selectedCities.length > 0;
    const hasZones = selectedZones.length > 0;
    const hasZipCodes =
      action === SHIP_TO_ACTIONS.ADD ? parseCommaSeparated(zipCodesInput).length > 0 : selectedZipCodes.length > 0;
    const hasExcludedStates =
      action === SHIP_TO_ACTIONS.ADD ? selectedExcludedStates.length > 0 : selectedExcludedStatesForRemoval.length > 0;
    return hasStates || hasCities || hasZones || hasZipCodes || hasExcludedStates;
  }, [
    statesControl,
    selectedStatesForRemoval,
    citiesInput,
    selectedCities,
    selectedZones,
    zipCodesInput,
    selectedZipCodes,
    selectedExcludedStates,
    selectedExcludedStatesForRemoval,
    action,
  ]);

  const getDisabledTooltipMessage = (): string => {
    if (action === SHIP_TO_ACTIONS.ADD) {
      if (shouldShowStates) return 'Select states to add';
      if (shouldShowZones) return 'Select zones to enable';
      if (shouldShowZipCodes) return 'Enter zip codes to add';
      if (shouldShowExcludedStates) return 'Enter states to exclude';
    } else {
      if (shouldShowStates && statesInAnyMethod.length === 0) return 'No states available to remove';
      if (shouldShowStates) return 'Select states to remove';
      if (shouldShowZones && zonesEnabledInAny.length === 0) return 'No zones available to disable';
      if (shouldShowZones) return 'Select zones to disable';
      if (shouldShowZipCodes && zipCodesInAnyMethod.length === 0) return 'No zip codes available to remove';
      if (shouldShowZipCodes) return 'Select zip codes to remove';
      if (shouldShowExcludedStates && excludedStatesInAnyMethod.length === 0) return 'No excluded states to remove';
      if (shouldShowExcludedStates) return 'Select excluded states to remove';
    }
    return 'Select values to preview changes';
  };

  const handleStatesSelect = (value: string) => {
    try {
      const parsed = JSON.parse(value) as Record<string, string[]>;
      setStatesControl(parsed);
    } catch (error) {
      console.error('Failed to parse states from dropdown:', error);
      setStatesControl({});
    }
  };

  const handleActionChange = (option: RadioButtonOption) => {
    setAction(option.value as ShipToAction);
    setStatesControl({});
    setSelectedStatesForRemoval([]);
    setCitiesInput('');
    setSelectedCities([]);
    setSelectedZones([]);
    setZipCodesInput('');
    setSelectedZipCodes([]);
    setSelectedExcludedStates([]);
    setStateSearchInput('');
    setStateSuggestions([]);
    setSelectedExcludedStatesForRemoval([]);
  };

  const handlePreviewChanges = () => {
    const statesControlForSubmit =
      action === SHIP_TO_ACTIONS.ADD
        ? statesControl
        : selectedStatesForRemoval.reduce(
            (acc, { country, state }) => {
              if (!acc[country]) acc[country] = [];
              acc[country].push(state);
              return acc;
            },
            {} as Record<string, string[]>,
          );

    onPreviewChanges({
      action,
      shopCountryCode,
      statesControl: statesControlForSubmit,
      cities: action === SHIP_TO_ACTIONS.ADD ? parseCommaSeparated(citiesInput) : selectedCities,
      zones: selectedZones,
      zipCodes: action === SHIP_TO_ACTIONS.ADD ? parseCommaSeparated(zipCodesInput) : selectedZipCodes,
      excludedStates: action === SHIP_TO_ACTIONS.ADD ? selectedExcludedStates : selectedExcludedStatesForRemoval,
    });
  };

  const handleCityToggle = (city: string, checked: boolean) => {
    setSelectedCities((prev) => (checked ? [...prev, city] : prev.filter((c) => c !== city)));
  };

  const handleStateToggle = (item: StateItem, checked: boolean) => {
    setSelectedStatesForRemoval((prev) =>
      checked ? [...prev, item] : prev.filter((s) => !(s.country === item.country && s.state === item.state)),
    );
  };

  const handleZoneToggle = (zone: string, checked: boolean) => {
    setSelectedZones((prev) => (checked ? [...prev, zone] : prev.filter((z) => z !== zone)));
  };

  const handleZipCodeToggle = (zipCode: string, checked: boolean) => {
    setSelectedZipCodes((prev) => (checked ? [...prev, zipCode] : prev.filter((z) => z !== zipCode)));
  };

  const handleExcludedStateToggle = (state: string, checked: boolean) => {
    setSelectedExcludedStatesForRemoval((prev) => (checked ? [...prev, state] : prev.filter((s) => s !== state)));
  };

  const preselectedValue = JSON.stringify(statesControl);

  return (
    <div className="bulk-edit-modal__view">
      <div className="bulk-edit-modal__header">
        <div className="bulk-edit-modal__header__left">
          <Button icon="back" variant="ghost" size="small" onClick={onBack} />
          <h2>Ship to</h2>
        </div>
        <Button icon="close" variant="ghost" size="small" onClick={onClose} />
      </div>
      <div className="bulk-edit-modal__content">
        {hasUnsupportedMethods ? (
          <div className="bulk-edit-modal__unsupported">
            <h3 className="bulk-edit-modal__unsupported-title">Coming Soon</h3>
            <p className="bulk-edit-modal__unsupported-description">
              Bulk editing for this delivery area type is not yet available. Please edit these methods individually.
            </p>
          </div>
        ) : (
          <>
            <div className="bulk-edit-modal__field">
              <label className="bulk-edit-modal__label">What Do You Want To Do?</label>
              <RadioButtonGroup
                options={getActionOptions(deliveryAreaMode)}
                defaultOptionValue="add"
                onChange={handleActionChange}
                disabled={false}
              />
            </div>

            {shouldShowStates && (
              <div className="bulk-edit-modal__field">
                <label className="bulk-edit-modal__label">{isInternational ? 'Countries' : 'States'}</label>
                {action === SHIP_TO_ACTIONS.ADD || isInternational ? (
                  <CountriesAndRegionsDropdown
                    multiSelect={true}
                    preselectedCountriesAndRegionsValue={preselectedValue}
                    onSelect={handleStatesSelect}
                    countries={isInternational ? [] : shopCountryCode ? [shopCountryCode] : []}
                  />
                ) : statesInAnyMethod.length > 0 ? (
                  <div className="bulk-edit-modal__checkbox-list">
                    {statesInAnyMethod.map((item) => (
                      <label key={`${item.country}:${item.state}`} className="bulk-edit-modal__checkbox-item">
                        <Checkbox
                          checked={selectedStatesForRemoval.some(
                            (s) => s.country === item.country && s.state === item.state,
                          )}
                          onChange={(checked) => handleStateToggle(item, checked)}
                        />
                        <span>{item.label}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <span className="bulk-edit-modal__hint">No states configured in selected methods</span>
                )}
              </div>
            )}

            {shouldShowCities && !isInternational && (
              <div className="bulk-edit-modal__field">
                <label className="bulk-edit-modal__label">Cities</label>
                {action === SHIP_TO_ACTIONS.ADD ? (
                  <>
                    <Input
                      placeholder="Cities..."
                      type="text"
                      value={citiesInput}
                      onChange={(e) => setCitiesInput(e.target.value)}
                    />
                    <span className="bulk-edit-modal__hint">Separate multiple values with commas</span>
                  </>
                ) : citiesInAnyMethod.length > 0 ? (
                  <div className="bulk-edit-modal__checkbox-list">
                    {citiesInAnyMethod.map((city) => (
                      <label key={city} className="bulk-edit-modal__checkbox-item">
                        <Checkbox
                          checked={selectedCities.includes(city)}
                          onChange={(checked) => handleCityToggle(city, checked)}
                        />
                        <span>{city}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <span className="bulk-edit-modal__hint">No cities configured in selected methods</span>
                )}
              </div>
            )}

            {shouldShowZones && (
              <div className="bulk-edit-modal__field">
                <label className="bulk-edit-modal__label">
                  Zones {action === SHIP_TO_ACTIONS.ADD ? '(Enable)' : '(Disable)'}
                </label>
                {(() => {
                  const zonesToShow = action === SHIP_TO_ACTIONS.ADD ? zonesForAdd : zonesEnabledInAny;
                  if (zonesToShow.length === 0) {
                    return (
                      <span className="bulk-edit-modal__hint">
                        {action === SHIP_TO_ACTIONS.ADD
                          ? 'All zones are already enabled in all selected methods'
                          : 'No zones are enabled in selected methods'}
                      </span>
                    );
                  }
                  return (
                    <div className="bulk-edit-modal__checkbox-list">
                      {zonesToShow.map((zone) => (
                        <label key={zone} className="bulk-edit-modal__checkbox-item">
                          <Checkbox
                            checked={selectedZones.includes(zone)}
                            onChange={(checked) => handleZoneToggle(zone, checked)}
                          />
                          <span>{zone}</span>
                        </label>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {shouldShowZipCodes && (
              <div className="bulk-edit-modal__field">
                <label className="bulk-edit-modal__label">
                  Zip Codes
                  {zipCodesOperator && (
                    <span className="bulk-edit-modal__label-badge">
                      {zipCodesOperator === 'includes' ? 'Include List' : 'Exclude List'}
                    </span>
                  )}
                </label>
                {zipCodesOperator && (
                  <>
                    <Divider />
                    <div className="bulk-edit-modal__alert bulk-edit-modal__alert--info bulk-edit-modal__alert--small bulk-edit-modal__alert--no-border">
                      <i className="icon icon-more_information" />
                      <span>
                        {zipCodesOperator === 'includes'
                          ? 'These methods only deliver TO the listed zip codes'
                          : 'These methods deliver everywhere EXCEPT the listed zip codes'}
                      </span>
                    </div>
                  </>
                )}
                {action === SHIP_TO_ACTIONS.ADD ? (
                  <Input
                    placeholder="Enter zip codes separated by commas, for example: 10112, 10023, 10500-10550, U1K 2KK, L1H*, *202*"
                    type="text"
                    value={zipCodesInput}
                    onChange={(e) => setZipCodesInput(e.target.value)}
                  />
                ) : zipCodesInAnyMethod.length > 0 ? (
                  <div className="bulk-edit-modal__checkbox-list">
                    {zipCodesInAnyMethod.map((zipCode) => (
                      <label key={zipCode} className="bulk-edit-modal__checkbox-item">
                        <Checkbox
                          checked={selectedZipCodes.includes(zipCode)}
                          onChange={(checked) => handleZipCodeToggle(zipCode, checked)}
                        />
                        <span>{zipCode}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <span className="bulk-edit-modal__hint">No zip codes configured in selected methods</span>
                )}
              </div>
            )}

            {shouldShowExcludedStates && (
              <div className="bulk-edit-modal__field">
                <label className="bulk-edit-modal__label">States to Exclude</label>
                <Divider />
                <div className="bulk-edit-modal__alert bulk-edit-modal__alert--info bulk-edit-modal__alert--small bulk-edit-modal__alert--no-border">
                  <i className="icon icon-more_information" />
                  <span>
                    {action === SHIP_TO_ACTIONS.ADD
                      ? 'These methods deliver everywhere EXCEPT the excluded states'
                      : 'Removing exclusions will allow delivery to those states again'}
                  </span>
                </div>
                {action === SHIP_TO_ACTIONS.ADD ? (
                  <div className="state-picker">
                    {selectedExcludedStates.length > 0 && (
                      <div className="state-picker__chips">
                        {selectedExcludedStates.map((state) => (
                          <div key={state} className="state-picker__chip">
                            <span>{state}</span>
                            <button type="button" onClick={() => handleRemoveSelectedState(state)}>
                              <i className="icon icon-close" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="state-picker__input-wrapper">
                      <Input
                        placeholder="Search for a state to exclude..."
                        type="text"
                        value={stateSearchInput}
                        onChange={(e) => setStateSearchInput(e.target.value)}
                      />
                      {stateSuggestions.length > 0 && (
                        <div className="state-picker__suggestions">
                          {stateSuggestions.map((suggestion) => (
                            <div
                              key={suggestion.placeId}
                              className="state-picker__suggestion"
                              onClick={() => handleSelectStateSuggestion(suggestion.placeId)}>
                              {suggestion.description}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : excludedStatesInAnyMethod.length > 0 ? (
                  <div className="bulk-edit-modal__checkbox-list">
                    {excludedStatesInAnyMethod.map((state) => (
                      <label key={state} className="bulk-edit-modal__checkbox-item">
                        <Checkbox
                          checked={selectedExcludedStatesForRemoval.includes(state)}
                          onChange={(checked) => handleExcludedStateToggle(state, checked)}
                        />
                        <span>{state}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <span className="bulk-edit-modal__hint">No excluded states configured in selected methods</span>
                )}
              </div>
            )}

            <Divider />
            <div className="bulk-edit-modal__alert bulk-edit-modal__alert--info bulk-edit-modal__alert--no-border">
              <i className="icon icon-more_information" />
              <span>{getInfoMessage(deliveryAreaMode, action)}</span>
            </div>
          </>
        )}
      </div>
      <div className="bulk-edit-modal__footer">
        <Button text="Back" onClick={onBack} size="medium" variant="secondary" />
        {!hasChanges && !hasUnsupportedMethods ? (
          <Tooltip tooltipText={getDisabledTooltipMessage()} maxWidth={220}>
            <Button text="Preview Changes" onClick={handlePreviewChanges} size="medium" variant="primary" disabled />
          </Tooltip>
        ) : (
          <div className="bulk-edit-modal__button-wrapper">
            <Button
              text="Preview Changes"
              onClick={handlePreviewChanges}
              size="medium"
              variant="primary"
              disabled={hasUnsupportedMethods}
            />
          </div>
        )}
      </div>
    </div>
  );
};
